import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { HeroCarousel } from "@/components/HeroCarousel";
import { MovieCard, MovieCardSkeleton } from "@/components/MovieCard";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Search, Sparkles, ChevronRight, Film } from "lucide-react";

export const Dashboard = () => {
  const [latestMovies, setLatestMovies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestMovies = async () => {
      try {
        const response = await axios.get(`${API}/movies/latest`);
        setLatestMovies(response.data.movies || []);
      } catch (error) {
        console.error("Error fetching movies:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestMovies();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="dashboard">
      <Navbar />
      
      {/* Hero Carousel */}
      <HeroCarousel movies={latestMovies} />

      {/* Quick Actions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
          <Link to="/search" className="group">
            <div className="glass rounded-xl p-6 flex items-center gap-4 transition-all duration-300 hover:border-violet-500/50 cursor-pointer">
              <div className="p-3 rounded-lg bg-violet-500/20">
                <Search className="w-6 h-6 text-violet-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white group-hover:text-violet-400 transition-colors">
                  Rechercher des films
                </h3>
                <p className="text-sm text-gray-400">Par titre, acteur ou réalisateur</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-violet-400 transition-colors" />
            </div>
          </Link>

          <Link to="/ai-search" className="group">
            <div className="glass rounded-xl p-6 flex items-center gap-4 transition-all duration-300 hover:border-pink-500/50 cursor-pointer">
              <div className="p-3 rounded-lg bg-gradient-to-br from-violet-500/20 to-pink-500/20">
                <Sparkles className="w-6 h-6 text-pink-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white group-hover:text-pink-400 transition-colors">
                  Recherche IA
                </h3>
                <p className="text-sm text-gray-400">Découvrez des films avec l'IA</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-pink-400 transition-colors" />
            </div>
          </Link>
        </div>
      </div>

      {/* Latest Movies Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Film className="w-6 h-6 text-violet-500" />
            <h2 className="font-['Outfit'] text-2xl font-bold text-white">
              Derniers ajouts
            </h2>
          </div>
          <Link to="/search">
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              Voir tout
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {[...Array(10)].map((_, i) => (
              <MovieCardSkeleton key={i} />
            ))}
          </div>
        ) : latestMovies.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.movie_id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Aucun film dans la bibliothèque
            </h3>
            <p className="text-gray-500">
              Ajoutez des fichiers vidéo dans le dossier média pour commencer.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
